import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import {
    Typography,
    Paper,
    CircularProgress,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Collapse,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    ErrorOutline as ErrorOutlineIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    HourglassEmpty as HourglassEmptyIcon,
} from '@material-ui/icons';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { EMPTY_ROW_MSG, ERROR_500, ERROR_MSG } from '../../../shared/lib/Localization';
import {
    getInventorySnapshotStockOrderTasks,
    getInventorySnapshotStockOrderTaskItems,
} from '../../api';
import { CustomTableTitle, ErrorStatusBar } from '../../../shared/components';
import GlobalStyles from '../../../../styles/GlobalStyles.web';
import { SNAPSHOT_TASK_ITEM_STATUS_OPTIONS, SnapshotTaskItemStatus, SnapshotTaskStatus } from './snapshotTaskStatus';
import { blue, green, red, orange, grey } from '@material-ui/core/colors';
import moment from 'moment';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SnapshotTask {
    id: number;
    snapshot_id: number;
    snapshot_number: string;
    task_id: string;
    status: string;
    progress_message: string | null;
    result_json: any;
    items_count: number;
    items_success_count: number;
    items_failure_count: number;
    created_at: string;
    updated_at: string;
}

interface SnapshotTaskItem {
    id: number;
    task: number;
    location_name: string;
    supplier_name: string;
    status: string;
    error_message: string | null;
    stock_order_uuid: string | null;
    stock_order_number: string | null;
    snapshot_items: SnapshotTaskItemProduct[];
    created_at: string;
    updated_at: string;
}

interface SnapshotTaskItemProduct {
    product_uuid: string;
    name: string;
    product_number: string | null;
    quantity: number;
    /** Quantity actually sent to Korona (same as quantity for new tasks) */
    quantity_requested?: number | null;
    case_quantity: number | null;
    /** True if item exists in synced stock order; null for legacy tasks */
    exists_in_stock_order?: boolean | null;
    /** Quantity in remote Korona stock order */
    remote_quantity?: number | null;
    stock_order_item_uuid?: string | null;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(2),
        },
        pageTitle: {
            marginBottom: theme.spacing(2),
        },
        taskItemsContainer: {
            padding: theme.spacing(2),
            marginTop: theme.spacing(1),
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
        },
        taskItemsTitle: {
            marginBottom: theme.spacing(1),
            fontWeight: 600,
        },
        filterRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        statusChip: {
            fontWeight: 600,
            fontSize: '0.75rem',
        },
        errorText: {
            color: red[700],
            fontSize: '0.8rem',
            maxWidth: 300,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        },
        summaryChips: {
            display: 'flex',
            gap: theme.spacing(0.5),
            flexWrap: 'wrap',
        },
        progressMessageBox: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        progressMessageLabel: {
            fontWeight: 600,
            marginBottom: theme.spacing(0.5),
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
        },
    })
);

// ─── Status Chip ─────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
    [SnapshotTaskStatus.SUCCESS]: green[600],
    [SnapshotTaskStatus.FAILURE]: red[600],
    [SnapshotTaskStatus.PENDING]: grey[500],
    [SnapshotTaskStatus.STARTED]: blue[600],
    [SnapshotTaskStatus.PROGRESS]: blue[400],
    [SnapshotTaskStatus.PARTIAL_SUCCESS]: orange[600],
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const color = STATUS_COLORS[status] || grey[500];
    return (
        <Chip
            label={status}
            size="small"
            style={{
                backgroundColor: color,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.72rem',
            }}
        />
    );
};

// ─── Product List Sub-table ──────────────────────────────────────────────────

const ProductListTable: React.FC<{ items: SnapshotTaskItemProduct[] }> = ({ items }) => {
    const displayQty = (row: SnapshotTaskItemProduct) =>
        row.quantity_requested != null ? row.quantity_requested : row.quantity;

    return (
        <Box margin={2} style={{ maxWidth: 900 }}>
            <Typography variant="subtitle2" gutterBottom>
                Products in this Stock Order Task
            </Typography>
            <MaterialTable
                columns={[
                    { title: 'Product Name', field: 'name' },
                    { title: 'Product Number', field: 'product_number' },
                    {
                        title: 'Quantity',
                        field: 'quantity',
                        type: 'numeric',
                        render: (row: SnapshotTaskItemProduct) => displayQty(row),
                    },
                    { title: 'Case Qty', field: 'case_quantity', type: 'numeric' },
                    {
                        title: 'In Stock Order',
                        field: 'exists_in_stock_order',
                        width: 140,
                        render: (row: SnapshotTaskItemProduct) => {
                            if (row.exists_in_stock_order == null) {
                                return (
                                    <Typography variant="body2" color="textSecondary">
                                        —
                                    </Typography>
                                );
                            }
                            if (row.exists_in_stock_order) {
                                return (
                                    <Tooltip title="Item exists in Korona stock order">
                                        <Box display="flex" alignItems="center" component="span">
                                            <CheckCircleOutlineIcon style={{ color: green[600], fontSize: 20 }} />
                                            {row.remote_quantity != null && (
                                                <Typography variant="body2" style={{ marginLeft: 4 }}>
                                                    (qty: {row.remote_quantity})
                                                </Typography>
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            }
                            return (
                                <Tooltip title="Not found in synced stock order">
                                    <Box display="flex" alignItems="center" component="span">
                                        <ErrorOutlineIcon style={{ color: orange[700], fontSize: 20 }} />
                                        <Typography variant="body2" color="error" style={{ marginLeft: 4 }}>
                                            Not found
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            );
                        },
                    },
                ]}
                data={items}
                options={{
                    toolbar: false,
                    paging: false,
                    search: false,
                    sorting: true,
                    draggable: false,
                    headerStyle: { backgroundColor: '#f5f5f5', padding: '8px 16px' },
                    rowStyle: { padding: '4px 16px' },
                }}
                style={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}
            />
        </Box>
    );
};

// ─── Task Items Sub-table ─────────────────────────────────────────────────────

interface TaskItemsTableProps {
    taskId: number;
    classes: ReturnType<typeof useStyles>;
}

const TaskItemsTable: React.FC<TaskItemsTableProps> = ({ taskId, classes }) => {
    const [items, setItems] = useState<SnapshotTaskItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    // Filter by stock_order_number instead of UUID for UI consistency
    const [stockOrderNumberFilter, setStockOrderNumberFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [orderBy, setOrderBy] = useState('');

    const fetchItems = useCallback(() => {
        setLoading(true);
        setError(null);
        getInventorySnapshotStockOrderTaskItems(
            taskId,
            page + 1,
            pageSize,
            orderBy || undefined,
            statusFilter || undefined,
            undefined, // stockOrderUuid
            stockOrderNumberFilter || undefined
        )
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setItems(data.results || data);
                setTotalCount(data.count || (data.results || data).length);
                setLoading(false);
            })
            .catch((err: any) => {
                handleError(err);
                setError(ERROR_MSG);
                setLoading(false);
            });
    }, [taskId, page, pageSize, orderBy, statusFilter, stockOrderNumberFilter]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const columns = [
        {
            title: 'Location',
            field: 'location_name',
            width: 180,
        },
        {
            title: 'Supplier',
            field: 'supplier_name',
            width: 200,
        },
        {
            title: 'Status',
            field: 'status',
            width: 120,
            render: (row: SnapshotTaskItem) => <StatusChip status={row.status} />,
        },
        {
            title: 'Stock Order',
            field: 'stock_order_number',
            width: 180,
            render: (row: SnapshotTaskItem) => {
                if (!row.stock_order_number) return <Typography variant="body2" color="textSecondary">—</Typography>;
                return (
                    <a
                        href={`/web/stock-order/list?search=${encodeURIComponent(row.stock_order_number)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: '#1976d2',
                            textDecoration: 'none',
                            fontWeight: 500,
                            fontFamily: 'monospace',
                            fontSize: '0.85rem'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {row.stock_order_number}
                    </a>
                );
            },
        },
        {
            title: 'Error Message',
            field: 'error_message',
            render: (row: SnapshotTaskItem) =>
                row.error_message ? (
                    <Tooltip title={row.error_message} placement="top-start">
                        <Typography className={classes.errorText}>
                            {row.error_message.length > 80
                                ? row.error_message.substring(0, 80) + '…'
                                : row.error_message}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                ),
        },
        {
            title: 'Created',
            field: 'created_at',
            width: 160,
            render: (row: SnapshotTaskItem) =>
                moment(row.created_at).format('DD MMM YYYY HH:mm'),
        },
    ];

    return (
        <div className={classes.taskItemsContainer}>
            <Typography variant="subtitle2" className={classes.taskItemsTitle}>
                Task Items
            </Typography>
            <div className={classes.filterRow}>
                <TextField
                    label="Filter by Stock Order #"
                    variant="outlined"
                    size="small"
                    value={stockOrderNumberFilter}
                    onChange={(e) => { setStockOrderNumberFilter(e.target.value); setPage(0); }}
                    InputProps={{
                        endAdornment: stockOrderNumberFilter ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => { setStockOrderNumberFilter(''); setPage(0); }}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                    style={{ minWidth: 220 }}
                />
                <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                    <InputLabel id="task-item-status-filter-label">Filter by Status</InputLabel>
                    <Select
                        labelId="task-item-status-filter-label"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value as string); setPage(0); }}
                        label="Filter by Status"
                    >
                        {SNAPSHOT_TASK_ITEM_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || '_all'} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {error && <ErrorStatusBar message={error} />}
            <MaterialTable
                columns={columns}
                data={items}
                isLoading={loading}
                options={{
                    toolbar: false,
                    paging: true,
                    pageSize,
                    pageSizeOptions: [10, 20, 50],
                    search: false,
                    sorting: true,
                    draggable: false,
                    emptyRowsWhenPaging: false,
                    rowStyle: (row: SnapshotTaskItem) => ({
                        backgroundColor: row.status === SnapshotTaskItemStatus.FAILURE ? '#fff5f5' : 'inherit',
                    }),
                }}
                detailPanel={[
                    {
                        tooltip: 'Show products',
                        render: (row: SnapshotTaskItem) => (
                            <ProductListTable items={row.snapshot_items || []} />
                        ),
                    },
                ]}
                onOrderChange={(colIdx, dir) => {
                    if (colIdx < 0) { setOrderBy(''); return; }
                    const col = columns[colIdx];
                    if (col && col.field) {
                        setOrderBy(dir === 'asc' ? col.field : `-${col.field}`);
                    }
                }}
                onChangePage={(p) => setPage(p)}
                totalCount={totalCount}
                page={page}
                localization={{ body: { emptyDataSourceMessage: loading ? 'Loading…' : EMPTY_ROW_MSG } }}
            />
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
    userData: any;
    globalData: any;
}

const NON_TERMINAL_STATUSES = [
    SnapshotTaskStatus.PENDING,
    SnapshotTaskStatus.STARTED,
    SnapshotTaskStatus.PROGRESS,
];

const SnapshotTaskHistoryScreen: React.FC<Props> = ({ userData, globalData }) => {
    const classes = useStyles();
    const tableRef = useRef<any>(null);
    const latestTasksRef = useRef<SnapshotTask[]>([]);
    const pollingRef = useRef<any>(null);
    const [dataVersion, setDataVersion] = useState(0);

    const [snapshotFilter, setSnapshotFilter] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(
        (query: {
            page: number;
            pageSize: number;
            orderBy?: { field?: string };
            orderDirection?: string;
        }) => {
            const orderByField = query.orderBy?.field || 'created_at';
            const orderDir = query.orderDirection || 'desc';
            const ordering = (orderDir === 'desc' ? '-' : '') + orderByField;
            return getInventorySnapshotStockOrderTasks(
                query.page + 1,
                query.pageSize,
                ordering,
                undefined,
                snapshotFilter || undefined,
                searchFilter || undefined
            )
                .then(checkStatus)
                .then(parseJSON)
                .then((data: any) => {
                    const results = data.results || data;
                    latestTasksRef.current = results;
                    setError(null);
                    setDataVersion((v) => v + 1);
                    return {
                        data: results,
                        page: query.page,
                        totalCount: data.count ?? results.length,
                    };
                })
                .catch((err: any) => {
                    handleError(err);
                    setError(ERROR_MSG);
                    throw err;
                });
        },
        [snapshotFilter, searchFilter]
    );

    // Auto-poll every 4 s while any task is in a non-terminal state
    useEffect(() => {
        const hasActiveTasks = latestTasksRef.current.some((t) =>
            NON_TERMINAL_STATUSES.includes(t.status as SnapshotTaskStatus)
        );
        if (hasActiveTasks && tableRef.current?.onQueryChange) {
            const q = tableRef.current.state?.query;
            if (q && !pollingRef.current) {
                pollingRef.current = setInterval(() => {
                    if (tableRef.current?.onQueryChange && tableRef.current.state?.query) {
                        tableRef.current.onQueryChange(tableRef.current.state.query);
                    }
                }, 4000);
            }
        } else if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [dataVersion]);

    // Refetch when filters change (reset to page 0)
    const isFirstMount = useRef(true);
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        if (tableRef.current?.onQueryChange && tableRef.current.state?.query) {
            tableRef.current.onQueryChange({ ...tableRef.current.state.query, page: 0 });
        }
    }, [snapshotFilter, searchFilter]);

    const columns = [
        {
            title: 'Snapshot Number',
            field: 'snapshot_number',
            width: 200,
            render: (row: SnapshotTask) => (
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {row.snapshot_number}
                </Typography>
            ),
        },
        {
            title: 'Status',
            field: 'status',
            width: 140,
            render: (row: SnapshotTask) => <StatusChip status={row.status} />,
        },
        {
            title: 'Summary',
            field: 'items_count',
            sorting: false,
            render: (row: SnapshotTask) => (
                <div className={classes.summaryChips}>
                    <Tooltip title="Total items">
                        <Chip
                            size="small"
                            label={`${row.items_count} total`}
                            style={{ backgroundColor: grey[200] }}
                        />
                    </Tooltip>
                    {row.items_success_count > 0 && (
                        <Tooltip title="Successful">
                            <Chip
                                size="small"
                                label={`${row.items_success_count} ✓`}
                                style={{ backgroundColor: green[100], color: green[800] }}
                            />
                        </Tooltip>
                    )}
                    {row.items_failure_count > 0 && (
                        <Tooltip title="Failed">
                            <Chip
                                size="small"
                                label={`${row.items_failure_count} ✗`}
                                style={{ backgroundColor: red[100], color: red[800] }}
                            />
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: 'Created',
            field: 'created_at',
            defaultSort: 'desc' as const,
            width: 160,
            render: (row: SnapshotTask) =>
                moment(row.created_at).format('DD MMM YYYY HH:mm'),
        },
        {
            title: 'Last Updated',
            field: 'updated_at',
            width: 160,
            render: (row: SnapshotTask) =>
                moment(row.updated_at).format('DD MMM YYYY HH:mm'),
        },
    ];

    return (
        <Container maxWidth={false} className={classes.root}>
            <Typography variant="h6" className={classes.pageTitle}>
                Inventory Replenishment Task History
            </Typography>

            <div className={classes.filterRow}>
                <TextField
                    label="Search Stock Order (UUID / Number)"
                    variant="outlined"
                    size="small"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: searchFilter ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchFilter('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                    style={{ minWidth: 320 }}
                />
                <TextField
                    label="Filter by Snapshot Number"
                    variant="outlined"
                    size="small"
                    value={snapshotFilter}
                    onChange={(e) => setSnapshotFilter(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: snapshotFilter ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSnapshotFilter('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                    style={{ minWidth: 240 }}
                />
            </div>

            {error && <ErrorStatusBar message={error} />}

            <MaterialTable
                tableRef={tableRef}
                columns={columns}
                data={fetchData}
                options={{
                    toolbar: false,
                    paging: true,
                    pageSize: 20,
                    pageSizeOptions: [10, 20, 50],
                    search: false,
                    sorting: true,
                    draggable: false,
                    rowStyle: (row: SnapshotTask) => ({
                        backgroundColor:
                            row.status === SnapshotTaskStatus.FAILURE
                                ? '#fff5f5'
                                : row.status === SnapshotTaskStatus.SUCCESS
                                    ? '#f5fff5'
                                    : 'inherit',
                    }),
                }}
                localization={{ body: { emptyDataSourceMessage: EMPTY_ROW_MSG } }}
                detailPanel={[
                    {
                        tooltip: 'Show task items',
                        render: (row: SnapshotTask) => (
                            <Box padding={2} paddingTop={0}>
                                {row.progress_message && (
                                    <Box className={classes.progressMessageBox}>
                                        <Typography
                                            variant="overline"
                                            className={classes.progressMessageLabel}
                                            component="div"
                                        >
                                            Progress
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            {row.progress_message}
                                        </Typography>
                                    </Box>
                                )}
                                <TaskItemsTable taskId={row.id} classes={classes} />
                            </Box>
                        ),
                    },
                ]}
            />
        </Container>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
    globalData: state.globalData,
});

export default connect(mapStateToProps)(SnapshotTaskHistoryScreen);
